import { Filter, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { VoiceTab } from "../hooks/useVoiceFiltering";

interface VoiceEmptyStateProps {
  selectedTab: VoiceTab;
  searchQuery: string;
  hasActiveFilters: boolean;
  isLoading: boolean;
  onClearSearch: () => void;
}

export const VoiceEmptyState = ({
  selectedTab,
  searchQuery,
  hasActiveFilters,
  isLoading,
  onClearSearch
}: VoiceEmptyStateProps) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  // All Voices tab - no filters applied
  if (selectedTab === "all" && !hasActiveFilters && !searchQuery) {
    return (
      <div className="text-center py-12">
        <div className="max-w-md mx-auto">
          <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#4E9CFF]/10 to-[#FF4DB8]/10 flex items-center justify-center mx-auto mb-4">
            <Filter className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Use filters or search to browse voices
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            We have over 2,000 voices available. Use the filters above or the search bar to narrow down your options and find the perfect voice.
          </p>
          <div className="flex flex-wrap gap-2 justify-center">
            <Badge variant="outline" className="text-xs">Try filtering by accent</Badge>
            <Badge variant="outline" className="text-xs">Or search by name</Badge>
            <Badge variant="outline" className="text-xs">Or use Recommended tab</Badge>
          </div>
        </div>
      </div>
    );
  }

  // Recommended tab - empty
  if (selectedTab === "recommended") {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 mb-4">No recommended voices available</p>
      </div>
    );
  }

  // Favorites tab - empty
  if (selectedTab === "favorites") {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 mb-4">
          {searchQuery ? `No favorites found matching "${searchQuery}"` : "No favorite voices yet"}
        </p>
        {searchQuery && (
          <Button variant="outline" onClick={onClearSearch}>
            Clear Search
          </Button>
        )}
      </div>
    );
  }

  // All Voices tab - filters applied but no results
  return (
    <div className="text-center py-12">
      <p className="text-gray-500 mb-4">
        {searchQuery ? `No voices found matching "${searchQuery}"` : "No voices match your filters"}
      </p>
      {searchQuery && (
        <Button variant="outline" onClick={onClearSearch}>
          Clear Search
        </Button>
      )}
    </div>
  );
};
